import * as React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Grid,
  Tooltip,
  Typography
} from "@mui/material";
import {useState} from "react";
import {ContextualHelpDialog} from "../help/ContextualHelpDialog";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {FormFab} from "../shared/FormFab";
import AddIcon from "@mui/icons-material/Add";
import {SubMix} from "./SubMix";
import {findIndexForFieldValue} from "../utils/ArrayUtils";
import {ConfirmDialog} from "../shared/ConfirmDialog";
import {PromptFormDialog} from "../shared/PromptFormDialog";
import {
  getSubMixDefaultName,
  getSubMixDescr, ingredientTypeGrain, ingredientTypeInclusion, ingredientTypeLiquid,
  SUBMIX_TYPE_PORRIDGE, SUBMIX_TYPE_SCALD,
  SUBMIX_TYPE_SOAKER
} from "../shared/Types";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import {generateIngredientId} from "../utils/StringUtils";
import {getSubmixComponentId} from "./SubmixSupport";

function SubMixForm(props) {
  const {formData, setFormData, updateIngredientList, componentId} = props
  const [openContextualHelpDialog, setOpenContextualHelpDialog] = useState(false)
  const [contextualHelpContent, setContextualHelpContent] = useState({title: undefined, content: undefined})
  const [candidateSubMixType, setCandidateSubMixType] = useState('')
  const [candidateSubMixName, setCandidateSubMixName] = useState('')
  const [actionableSubMix, setActionableSubMix] = useState(undefined)
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const [openNewDialog, setOpenNewDialog] = useState(false)

  function submixComponentId(submixData) {
    return getSubmixComponentId(componentId, submixData)
  }

  function onCloseHelp() {
    setOpenContextualHelpDialog(false)
    setContextualHelpContent({title: undefined, content: undefined})
  }

  function addNewSubmixClick(subMixType) {
    setCandidateSubMixType(subMixType)
    setCandidateSubMixName(getSubMixDefaultName(subMixType))
    setOpenNewDialog(true)
  }

  function addNewSubmixCancel() {
    setCandidateSubMixType('')
    setCandidateSubMixName('')
    setOpenNewDialog(false)
  }

  function createPorridgeSubmix(uniqueId) {
    return {
      name: candidateSubMixName?.trim(),
      mainPct: 20,
      absorbPct: 25,
      notes: 'Drain excess liquid or adjust hydro %',
      id: uniqueId,
      type: SUBMIX_TYPE_PORRIDGE,
      grain: [{
        name: 'Grain',
        percent: 100,
        percentStr: '100',
        id: 'sm.g1'
      }],
      dry: [],
      liquid: [{
        name: 'Water',
        percent: 100,
        percentStr: '100',
        id: 'sm.l1'
      }]
    }
  }

  function createScaldSubmix(uniqueId) {
    return {
      name: candidateSubMixName?.trim(),
      mainPct: 20,
      notes: 'Flour and liquid factor into the overall hydro %',
      id: uniqueId,
      type: SUBMIX_TYPE_SCALD,
      flour: [{
        name: 'Flour',
        percent: 100,
        percentStr: '100',
        id: 'sm.f1'
      }],
      liquid: [{
        name: 'Water',
        percent: 100,
        percentStr: '100',
        id: 'sm.l1'
      }]
    }
  }

  function createSoakerSubmix(uniqueId) {
    return {
      name: candidateSubMixName?.trim(),
      mainPct: 20,
      absorbPct: 25,
      notes: 'Drain excess liquid',
      id: uniqueId,
      type: SUBMIX_TYPE_SOAKER,
      dry: [{
        name: 'Dry',
        percent: 100,
        percentStr: '100',
        id: 'sm.d1'
      }],
      liquid: [{
        name: 'Water',
        percent: 100,
        percentStr: '100',
        id: 'sm.l1'
      }]
    }
  }

  function addNewSubMix() {
    const uniqueId = generateIngredientId('sm')
    let newSubMix
    let ingredients = []
    switch (candidateSubMixType) {
      case SUBMIX_TYPE_PORRIDGE:
        newSubMix = createPorridgeSubmix(uniqueId)
        ingredients = [{type: ingredientTypeGrain, collection: newSubMix.grain},
          {type: ingredientTypeInclusion, collection: newSubMix.dry},
          {type: ingredientTypeLiquid, collection: newSubMix.liquid}]
        break
      case SUBMIX_TYPE_SCALD:
        newSubMix = createScaldSubmix(uniqueId)
        ingredients = [{type: ingredientTypeGrain, collection: newSubMix.flour},
          {type: ingredientTypeLiquid, collection: newSubMix.liquid}]
        break
      case SUBMIX_TYPE_SOAKER:
        newSubMix = createSoakerSubmix(uniqueId)
        ingredients = [{type: ingredientTypeInclusion, collection: newSubMix.dry},
          {type: ingredientTypeLiquid, collection: newSubMix.liquid}]
        break
      default:
    }
    if (newSubMix !== undefined) {
      // add new submix to main form
      const subMixCopy = formData.submixes.slice()
      subMixCopy.push(newSubMix)
      setFormData({...formData, submixes: subMixCopy})
      // ... and update ingredient master
      updateIngredientList(submixComponentId(newSubMix), ingredients)
    }
  }

  function updateSubMix(subMix) {
    const ix = findIndexForFieldValue(formData.submixes, 'id', subMix['id'])
    if (ix < 0) {
      console.log(`!! updateSubMix: cannot find submix for id ${subMix['id']}`)
      return
    }
    const subMixCopy = formData.submixes.slice()
    subMixCopy[ix] = subMix
    setFormData({...formData, submixes: subMixCopy})
  }

  function deleteSubMixClick(subMix) {
    setActionableSubMix(subMix)
    setOpenDeleteConfirm(true)
  }

  function deleteSubMix(subMix) {
    setOpenDeleteConfirm(false)
    let subMixCopy = formData.submixes.filter((sm) => sm.id !== subMix.id)
    setFormData({...formData, submixes: subMixCopy})
    updateIngredientList(submixComponentId(subMix), [])
  }

  return (
          <div>
            <Grid item container direction={'column'} rowSpacing={1}>
              <Grid item container direction={'row'} xs={12} >
              <Tooltip title={`New ${getSubMixDescr(SUBMIX_TYPE_PORRIDGE)}`} placement={'top'} arrow>
                <FormFab sx={{marginTop: -1, marginRight: 2}} size="small" color="primary" aria-label="add">
                  <AddIcon onClick={() => addNewSubmixClick(SUBMIX_TYPE_PORRIDGE)}/>
                </FormFab>
              </Tooltip>
              <Typography fontSize={16} fontWeight={550}>{getSubMixDescr(SUBMIX_TYPE_PORRIDGE)}</Typography>
              </Grid>
              {formData.submixes.filter((sm) => sm.type === SUBMIX_TYPE_PORRIDGE)
                                .map((sm, idx) => {
                return (
                        <Grid item container direction={'column'} key={sm.id}>
                          <Grid item container direction={'row'} marginTop={0.5}>
                          <Accordion defaultExpanded={true}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                              aria-controls="panel1a-content"
                                              id="panel1a-header">
                              <Typography>{sm.name} ({sm.mainPct}%)</Typography>
                              <IconButton sx={{marginLeft: 1, marginTop: -1, marginBottom: -1}} size={'small'} color={'primary'} variant={'contained'}
                                          onClick={() => deleteSubMixClick(sm)}>
                                <DeleteIcon/>
                              </IconButton>
                            </AccordionSummary>
                            <AccordionDetails>
                              <SubMix subMixData={sm} updateSubMixData={updateSubMix} updateIngredientList={updateIngredientList} componentId={submixComponentId(sm)}/>
                            </AccordionDetails>
                          </Accordion>
                          </Grid>
                        </Grid>
                )
              })}
              <Grid item container direction={'row'}  marginTop={3} xs={12} >
                <Tooltip title={`New ${getSubMixDescr(SUBMIX_TYPE_SCALD)}`} placement={'top'} arrow>
                  <FormFab sx={{marginTop: -1, marginRight: 2}} size="small" color="primary" aria-label="add">
                    <AddIcon onClick={() => addNewSubmixClick(SUBMIX_TYPE_SCALD)}/>
                  </FormFab>
                </Tooltip>
                <Typography fontSize={16} fontWeight={550}>{getSubMixDescr(SUBMIX_TYPE_SCALD)}</Typography>
              </Grid>
              {formData.submixes.filter((sm) => sm.type === SUBMIX_TYPE_SCALD)
              .map((sm, idx) => {
                return (
                        <Grid item container direction={'column'} key={sm.id}>
                          <Grid item container direction={'row'} marginTop={0.5}>
                            <Accordion defaultExpanded={true}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header">
                                <Typography>{sm.name} ({sm.mainPct}%)</Typography>
                                <IconButton sx={{marginLeft: 1, marginTop: -1, marginBottom: -1}} size={'small'} color={'primary'} variant={'contained'}
                                            onClick={() => deleteSubMixClick(sm)}>
                                  <DeleteIcon/>
                                </IconButton>
                              </AccordionSummary>
                              <AccordionDetails>
                                <SubMix subMixData={sm} updateSubMixData={updateSubMix} updateIngredientList={updateIngredientList} componentId={submixComponentId(sm)}/>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        </Grid>
                )
              })}
              <Grid item container direction={'row'}  marginTop={3} xs={12} >
                <Tooltip title={`New ${getSubMixDescr(SUBMIX_TYPE_SOAKER)}`} placement={'top'} arrow>
                  <FormFab sx={{marginTop: -1, marginRight: 2}} size="small" color="primary" aria-label="add">
                    <AddIcon onClick={() => addNewSubmixClick(SUBMIX_TYPE_SOAKER)}/>
                  </FormFab>
                </Tooltip>
                <Typography fontSize={16} fontWeight={550}>{getSubMixDescr(SUBMIX_TYPE_SOAKER)}</Typography>
              </Grid>
              {formData.submixes.filter((sm) => sm.type === SUBMIX_TYPE_SOAKER)
              .map((sm, idx) => {
                return (
                        <Grid item container direction={'column'} key={sm.id}>
                          <Grid item container direction={'row'} marginTop={0.5}>
                            <Accordion defaultExpanded={true}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                                                aria-controls="panel1a-content"
                                                id="panel1a-header">
                                <Typography>{sm.name} ({sm.mainPct}%)</Typography>
                                <IconButton sx={{marginLeft: 1, marginTop: -1, marginBottom: -1}} size={'small'} color={'primary'} variant={'contained'}
                                            onClick={() => deleteSubMixClick(sm)}>
                                  <DeleteIcon/>
                                </IconButton>
                              </AccordionSummary>
                              <AccordionDetails>
                                <SubMix subMixData={sm} updateSubMixData={updateSubMix} updateIngredientList={updateIngredientList} componentId={submixComponentId(sm)}/>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        </Grid>
                )
              })}
              <ConfirmDialog title={`Deleting '${actionableSubMix !== undefined ? actionableSubMix.name : ''}'`}
                             confirmMessage={`Are you sure?`}
                             confirm={() => deleteSubMix(actionableSubMix)}
                             confirmButtonName={'Delete'}
                             cancel={() => setOpenDeleteConfirm(false)}
                             cancelButtonName={'Cancel'}
                             dialogOpen={openDeleteConfirm}/>
              <PromptFormDialog message={``}
                                fieldName={'Name'}
                                formValue={candidateSubMixName}
                                setFormValue={setCandidateSubMixName}
                                onAction1={addNewSubMix}
                                onCancel={addNewSubmixCancel}
                                dialogOpen={openNewDialog}
                                setDialogOpen={setOpenNewDialog}
                                action1ButtonLabel={'Continue'}/>
            <ContextualHelpDialog isOpen={openContextualHelpDialog} close={onCloseHelp}
                                  title={contextualHelpContent.title} content={contextualHelpContent.content}/>
            </Grid>
          </div>
  )
}

export { SubMixForm };